// File: components/search/SearchResults.js

import React, { useState, useEffect } from 'react';
import { highlightText, formatDate } from '../../utils/helpers';
import { ICONS, REGULATIONS, DATE_FORMATS } from '../../utils/constants';
import { searchSections } from '../../utils/dataLoader';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * SearchResults Component
 * 
 * Displays search results with highlighting, filtering, and sorting.
 * Mobile-first design with touch-optimized result cards.
 * 
 * @param {Object} props - Component props
 * @param {string} props.query - Search query
 * @param {Array} props.results - Search results array
 * @param {function} props.onResultClick - Handler for result click
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @param {boolean} props.showFilters - Show filter options
 * @param {string} props.sortBy - Sort option (relevance, date, code)
 * @param {function} props.onSortChange - Handler for sort change
 * @param {string} props.filterRegulation - Filter by regulation (1910, 1926, all)
 * @param {function} props.onFilterChange - Handler for filter change
 * @param {string} props.className - Additional CSS classes
 */
// Decode HTML entities (titles come as "&#xA7; 1910.23 Ladders.")
const decodeEntities = (text) => {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
};

// Strip the leading "§ 1910.23" from a title (the code is displayed separately)
const cleanTitle = (title) => {
    return decodeEntities(title)
        .replace(/^\s*§?\s*\d{4}\.\d+[a-zA-Z]?\s*[-–—:]?\s*/, '')
        .trim();
};

const SearchResults = ({
    query = '',
    results = null,
    onResultClick,
    loading = false,
    error = null,
    showFilters = true,
    sortBy = 'relevance',
    onSortChange,
    filterRegulation = 'all',
    onFilterChange,
    className = ''
}) => {
    const { t } = useLanguage();
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [internalSort, setInternalSort] = useState(sortBy);
    const [internalFilter, setInternalFilter] = useState(filterRegulation);

    // Sort/filter work standalone when no external handlers are provided
    const effectiveSort = onSortChange ? sortBy : internalSort;
    const effectiveFilter = onFilterChange ? filterRegulation : internalFilter;

    const handleSortChange = (value) => {
        if (onSortChange) onSortChange(value);
        else setInternalSort(value);
    };

    const handleFilterChange = (value) => {
        if (onFilterChange) onFilterChange(value);
        else setInternalFilter(value);
    };

    // Run the search when the query changes (unless results are supplied via props)
    useEffect(() => {
        if (results !== null) return;

        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            setSearchError(null);
            setSearching(false);
            return;
        }

        let cancelled = false;
        setSearching(true);
        setSearchError(null);

        // Small debounce so we don't search on every keystroke
        const timer = setTimeout(async () => {
            try {
                const response = await searchSections(query);

                if (cancelled) return;

                if (response.success) {
                    setSearchResults(response.results.map(result => ({
                        code: result.code,
                        title: cleanTitle(result.title),
                        regulationCode: result.regulation,
                        subpart: result.subpart?.letter || '',
                        lastUpdated: result.lastUpdated,
                        critical: result.critical,
                        snippet: result.snippet
                    })));
                } else {
                    setSearchResults([]);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Search failed:', err);
                    setSearchError(t('search.failed'));
                }
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query, results, t]);

    const effectiveResults = results !== null ? results : searchResults;
    const effectiveLoading = loading || searching;
    const effectiveError = error || searchError;

    // Filter results by regulation
    const filteredResults = effectiveFilter === 'all'
        ? effectiveResults
        : effectiveResults.filter(result => result.regulationCode === effectiveFilter);

    // Sort results
    const sortedResults = [...filteredResults];
    switch (effectiveSort) {
        case 'date':
            sortedResults.sort((a, b) => {
                const dateA = new Date(a.lastUpdated || 0);
                const dateB = new Date(b.lastUpdated || 0);
                return dateB - dateA;
            });
            break;

        case 'code':
            sortedResults.sort((a, b) => {
                const codeA = a.code || '';
                const codeB = b.code || '';
                return codeA.localeCompare(codeB, undefined, { numeric: true });
            });
            break;

        case 'relevance':
        default:
            // Already sorted by relevance from search
            break;
    }

    // Handle result click
    const handleResultClick = (result) => {
        if (onResultClick) {
            onResultClick(result);
        }
    };

    // Get regulation info (REGULATIONS is an object keyed by code)
    const getRegulationInfo = (regulationCode) => {
        return REGULATIONS[regulationCode] || { name: regulationCode, icon: ICONS.BOOK };
    };

    // Render loading state
    if (effectiveLoading) {
        return (
            <div className={`search-results loading ${className}`}>
                <div className="search-results-header">
                    <div className="spinner"></div>
                    <p>{t('search.searching')}</p>
                </div>
            </div>
        );
    }

    // Render error state
    if (effectiveError) {
        return (
            <div className={`search-results error ${className}`}>
                <div className="error-message">
                    <span className="icon">{ICONS.WARNING}</span>
                    <p>{effectiveError}</p>
                </div>
            </div>
        );
    }

    // Render empty state
    if (!query || query.trim().length < 2) {
        return (
            <div className={`search-results empty ${className}`}>
                <div className="empty-message">
                    <span className="icon">{ICONS.SEARCH}</span>
                    <p>{t('search.enterTerm')}</p>
                    <span className="hint">{t('search.hint')}</span>
                </div>
            </div>
        );
    }

    // Render no results
    if (sortedResults.length === 0) {
        return (
            <div className={`search-results no-results ${className}`}>
                <div className="no-results-message">
                    <span className="icon">{ICONS.SEARCH}</span>
                    <p>{t('search.noResults', { q: query })}</p>
                    <span className="hint">{t('search.noResultsHint')}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={`search-results ${className}`}>
            {/* Results Header */}
            <div className="search-results-header">
                <div className="results-count">
                    <span className="text">
                        {sortedResults.length === 1
                            ? t('search.resultFor', { q: query })
                            : t('search.resultsFor', { n: sortedResults.length, q: query })}
                    </span>
                </div>

                {/* Filters and Sort */}
                {showFilters && (
                    <div className="results-controls">
                        {/* Sort Dropdown */}
                        <div className="control-group">
                            <label htmlFor="sort-select">{t('search.sortBy')}</label>
                            <select
                                id="sort-select"
                                value={effectiveSort}
                                onChange={(e) => handleSortChange(e.target.value)}
                                className="sort-select"
                            >
                                <option value="relevance">{t('search.sort.relevance')}</option>
                                <option value="date">{t('search.sort.date')}</option>
                                <option value="code">{t('search.sort.code')}</option>
                            </select>
                        </div>

                        {/* Filter Dropdown */}
                        <div className="control-group">
                            <label htmlFor="filter-select">{t('search.filter.regulation')}</label>
                            <select
                                id="filter-select"
                                value={effectiveFilter}
                                onChange={(e) => handleFilterChange(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">{t('search.filter.all')}</option>
                                <option value="1910">29 CFR 1910</option>
                                <option value="1926">29 CFR 1926</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Results List */}
            <div className="search-results-list">
                {sortedResults.map((result, index) => {
                    const regInfo = getRegulationInfo(result.regulationCode);
                    
                    return (
                        <div
                            key={`${result.code}-${index}`}
                            className="search-result-item"
                            onClick={() => handleResultClick(result)}
                        >
                            {/* Result Header */}
                            <div className="result-header">
                                <div className="result-regulation">
                                    <span className="icon">{regInfo.icon}</span>
                                    <span className="reg-code">29 CFR {result.regulationCode}</span>
                                </div>
                                {result.critical && (
                                    <span className="badge critical">{t('search.critical')}</span>
                                )}
                            </div>

                            {/* Result Title */}
                            <div className="result-title">
                                <span className="code-number">{result.code}</span>
                                <span className="separator">-</span>
                                <span 
                                    className="title-text"
                                    dangerouslySetInnerHTML={{ 
                                        __html: highlightText(result.title, query) 
                                    }}
                                />
                            </div>

                            {/* Result Snippet */}
                            {result.snippet && (
                                <div 
                                    className="result-snippet"
                                    dangerouslySetInnerHTML={{ 
                                        __html: highlightText(result.snippet, query) 
                                    }}
                                />
                            )}

                            {/* Result Meta */}
                            <div className="result-meta">
                                {result.subpart && (
                                    <span className="subpart">
                                        {t('search.subpart', { s: result.subpart })}
                                    </span>
                                )}
                                {result.lastUpdated && (
                                    <>
                                        {result.subpart && <span className="separator">•</span>}
                                        <span className="date">
                                            {t('search.updated', { d: formatDate(new Date(result.lastUpdated), DATE_FORMATS.SHORT) })}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SearchResults;
